<!-- Product Groups Table Component -->
<div class="product-groups-table-wrapper">
  <p-table
    [value]="data?.Items || []"
    [loading]="loading"
    [paginator]="true"
    [rows]="data?.PageSize || 20"
    [totalRecords]="data?.TotalRecords || 0"
    [rowsPerPageOptions]="pageSizeOptions"
    [lazy]="true"
    [scrollable]="true"
    [scrollHeight]="scrollHeight"
    [showCurrentPageReport]="true"
    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} groups"
    (onLazyLoad)="onPage({ first: $event.first || 0, rows: $event.rows || 20 })"
    (onSort)="onSort({ field: $event.field || '', order: $event.order || 1 })"
    styleClass="p-datatable-sm product-groups-table"
    responsiveLayout="stack"
    [breakpoint]="'768px'"
    dataKey="ProductGroupId"
    [rowExpandMode]="'single'"
    [expandedRowKeys]="expandedRows"
    (onRowExpand)="onRowExpand($event)"
    (onRowCollapse)="onRowCollapse($event)"
  >
    <!-- Loading Body Template (Skeleton) -->
    <ng-template pTemplate="loadingbody">
      <app-table-skeleton-rows [rows]="10" [columns]="skeletonColumns" />
    </ng-template>

    <!-- Header Template -->
    <ng-template pTemplate="header">
      <tr>
        <!-- Expand Column -->
        <th style="width: 3rem"></th>

        <!-- ID Column -->
        <th pSortableColumn="ProductGroupId" style="width: 5rem">
          ID
          <p-sortIcon field="ProductGroupId" />
        </th>

        <!-- Name Column -->
        <th pSortableColumn="Name" style="min-width: 150px">
          Name
          <p-sortIcon field="Name" />
        </th>

        <!-- Products Count Column -->
        <th style="width: 100px">Products</th>

        <!-- Created Column -->
        <th pSortableColumn="CreatedUtc" style="width: 120px">
          Created
          <p-sortIcon field="CreatedUtc" />
        </th>

        <!-- Updated Column -->
        <th pSortableColumn="UpdatedUtc" style="width: 120px">
          Updated
          <p-sortIcon field="UpdatedUtc" />
        </th>

        <!-- Actions Column -->
        @if (showActions) {
          <th style="width: 3rem" class="actions-column"></th>
        }
      </tr>
    </ng-template>

    <!-- Body Template -->
    <ng-template pTemplate="body" let-group let-expanded="expanded">
      <tr
        class="group-row"
        [class.clickable-row]="group.Products?.length > 0"
        (click)="group.Products?.length > 0 && toggleRowExpansion(group)"
      >
        <!-- Expand Toggle -->
        <td
          class="text-center expand-column"
          (click)="$event.stopPropagation()"
        >
          @if (group.Products?.length > 0) {
            <p-button
              [pRowToggler]="group"
              type="button"
              pRipple
              [text]="true"
              [rounded]="true"
              [plain]="true"
              size="small"
              class="text-gray-400 hover:text-gray-600"
              [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
              pTooltip="Click to expand/collapse products"
              tooltipPosition="right"
            />
          }
        </td>

        <!-- ID Column -->
        <td class="id-column">
          <span class="p-column-title">ID</span>
          <a
            class="id-link font-medium"
            (click)="onEdit(group); $event.stopPropagation()"
            pTooltip="Edit group"
            tooltipPosition="top"
          >
            {{ group.ProductGroupId }}
          </a>
        </td>

        <!-- Name Column -->
        <td>
          <span class="p-column-title">Name</span>
          <span class="font-medium">{{ group.Name }}</span>
        </td>

        <!-- Products Count Column -->
        <td>
          <span class="p-column-title">Products</span>
          <span class="products-count-badge">
            {{ group.Products?.length || 0 }}
          </span>
        </td>

        <!-- Created Column -->
        <td>
          <span class="p-column-title">Created</span>
          <span class="text-600">{{ formatDate(group.CreatedUtc) }}</span>
        </td>

        <!-- Updated Column -->
        <td>
          <span class="p-column-title">Updated</span>
          <span class="text-600">{{ formatDate(group.UpdatedUtc) }}</span>
        </td>

        <!-- Actions Column -->
        @if (showActions) {
          <td class="actions-column" (click)="$event.stopPropagation()">
            <p-button
              icon="pi pi-ellipsis-v"
              [text]="true"
              [rounded]="true"
              size="small"
              (click)="onRowContextMenu($event, rowMenu, group)"
              pTooltip="Actions"
              tooltipPosition="left"
            />
            <p-menu
              #rowMenu
              [model]="contextMenuItems"
              [popup]="true"
              appendTo="body"
            />
          </td>
        }
      </tr>
    </ng-template>

    <!-- Row Expansion Template -->
    <ng-template pTemplate="expandedrow" let-group>
      <tr class="expanded-row">
        <td [attr.colspan]="showActions ? 7 : 6" class="expanded-row-cell">
          <div class="expanded-row-content">
            <!-- Quick Actions Section -->
            <div class="quick-actions-section">
              <p-button
                label="Edit"
                icon="pi pi-pencil"
                size="small"
                severity="secondary"
                [outlined]="true"
                (onClick)="onEdit(group)"
              />
              <p-button
                label="Delete"
                icon="pi pi-trash"
                size="small"
                severity="danger"
                [outlined]="true"
                (onClick)="onDelete(group)"
              />
            </div>

            <!-- Products Section -->
            @if (group.Products?.length > 0) {
              <div class="detail-section">
                <div class="section-header">
                  Products in this group ({{ group.Products.length }})
                </div>
                <div class="products-grid">
                  @for (
                    product of getDisplayedProducts(group);
                    track product.Id
                  ) {
                    <div class="product-item">
                      <span class="product-title">{{ product.Title }}</span>
                      <div class="product-actions">
                        <p-button
                          label="View"
                          icon="pi pi-eye"
                          size="small"
                          [text]="true"
                          (onClick)="onViewProduct(product)"
                          pTooltip="View product details"
                          tooltipPosition="top"
                        />
                        <p-button
                          label="Search"
                          icon="pi pi-search"
                          size="small"
                          [text]="true"
                          (onClick)="onSearchProduct(product)"
                          pTooltip="Search for this product"
                          tooltipPosition="top"
                        />
                      </div>
                    </div>
                  }
                </div>
                @if (group.Products?.length > 6) {
                  <div class="show-more-container">
                    <p-button
                      label="Show all {{ group.Products.length }} products"
                      icon="pi pi-list"
                      size="small"
                      [text]="true"
                      (onClick)="onEditGroup(group)"
                    />
                  </div>
                }
              </div>
            }
          </div>
        </td>
      </tr>
    </ng-template>

    <!-- Empty Message Template -->
    <ng-template pTemplate="emptymessage">
      <tr>
        <td [attr.colspan]="showActions ? 7 : 6" class="text-center py-5">
          <div
            class="flex flex-column align-items-center justify-content-center gap-3 py-5"
          >
            <i class="pi pi-folder-open text-4xl text-400"></i>
            <span class="text-600">No product groups found</span>
            <span class="text-500 text-sm"
              >Create a product group to get started</span
            >
          </div>
        </td>
      </tr>
    </ng-template>
  </p-table>
</div>
